import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { CheckCircle, Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

const schema = z.object({
  nom: z.string().min(2, "Nom requis"),
  prenom: z.string().min(2, "Prénom requis"),
  telephone: z.string().min(8, "Téléphone invalide"),
  email: z.string().email("Email invalide"),
  vehicule: z.string().min(2, "Véhicule requis"),
  service: z.string().min(1, "Service requis"),
  description: z.string().min(10, "Décrivez votre besoin (10 caractères min)"),
});

const SERVICES = ["Entretien & Révision", "Vidange", "Freinage", "Pneumatiques", "Diagnostic électronique", "Climatisation", "Batterie", "Distribution", "Carrosserie", "Contrôle technique", "Vitrage automobile", "Calibration ADAS", "Autre"];

export default function QuotePage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await api.post("/quotes", data);
      setSubmitted(true);
      toast.success("Demande de devis envoyée !");
    } catch {
      toast.error("Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-32 pb-20 px-6 md:px-12 min-h-[70vh] flex items-center bg-[#F8F9FA]">
        <div className="max-w-xl mx-auto text-center bg-white border border-[#E5E7EB] p-10 md:p-16">
          <CheckCircle size={64} weight="duotone" className="text-[#E63946] mx-auto" />
          <h1 className="mt-6 text-3xl font-bold text-[#0D2C54]">Demande reçue</h1>
          <p className="mt-4 text-[#555555]">Notre équipe vous transmet un devis détaillé sous 24h ouvrées.</p>
          <Link to="/"><Button data-testid="quote-back-home" className="mt-8 bg-[#0D2C54] hover:bg-[#0A2242] text-white rounded-none h-12 px-8">Retour à l'accueil</Button></Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="Devis gratuit — MECA AUTO 17 Tonnay-Charente"
        description="Demandez votre devis automobile gratuit en ligne chez MECA AUTO 17. Réponse rapide sous 24h, tarifs transparents, sans engagement. Tonnay-Charente 17430."
      />
      <section className="pt-32 pb-20 px-6 md:px-12 bg-[#F8F9FA]">
        <div className="max-w-[1100px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline text-[#E63946]">Devis gratuit</span>
            <h1 className="mt-4 text-4xl lg:text-5xl font-bold text-[#0D2C54] tracking-tight">
              Recevez votre devis sous 24h.
            </h1>
            <p className="mt-6 text-[#555555] leading-relaxed">
              Décrivez-nous votre besoin : nous vous transmettons un devis détaillé, sans engagement et 100% transparent.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} data-testid="quote-form" className="lg:col-span-7 bg-white border border-[#E5E7EB] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-[#E5E7EB]">
              <Receipt size={24} className="text-[#E63946]" weight="duotone" />
              <h2 className="font-display text-xl font-semibold text-[#0D2C54]">Votre demande</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Nom *" error={errors.nom}>
                <Input data-testid="quote-nom" {...register("nom")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Prénom *" error={errors.prenom}>
                <Input data-testid="quote-prenom" {...register("prenom")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Téléphone *" error={errors.telephone}>
                <Input data-testid="quote-tel" {...register("telephone")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <Field label="Email *" error={errors.email}>
                <Input data-testid="quote-email" type="email" {...register("email")} className="rounded-none border-[#E5E7EB] h-12" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Marque, modèle, année *" error={errors.vehicule}>
                  <Input data-testid="quote-vehicule" placeholder="Ex: Peugeot 308 - 2018 - 95 000 km" {...register("vehicule")} className="rounded-none border-[#E5E7EB] h-12" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Service souhaité *" error={errors.service}>
                  <Select onValueChange={(v) => setValue("service", v)}>
                    <SelectTrigger data-testid="quote-service" className="rounded-none border-[#E5E7EB] h-12">
                      <SelectValue placeholder="Choisir un service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <input type="hidden" {...register("service")} value={watch("service") || ""} />
                </Field>
              </div>
            </div>

            <div className="mt-5">
              <Field label="Description détaillée *" error={errors.description}>
                <Textarea data-testid="quote-description" rows={5} {...register("description")} className="rounded-none border-[#E5E7EB] resize-none" placeholder="Décrivez les symptômes, l'intervention souhaitée, vos contraintes..." />
              </Field>
            </div>

            <Button type="submit" data-testid="quote-submit" disabled={loading} className="mt-8 w-full bg-[#E63946] hover:bg-[#D62839] text-white rounded-none h-14 font-semibold tracking-wide">
              {loading ? "Envoi..." : "Demander mon devis gratuit"}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="text-xs overline text-[#0D2C54] mb-2 block">{label}</Label>
      {children}
      {error && <p className="text-xs text-[#E63946] mt-1">{error.message}</p>}
    </div>
  );
}
